<template lang="pug">
v-card
  title-bar(dark) Review Status For SMS Consent
  v-layout.px-4.pt-4.pb-2(column, align-center, justify-start)
    v-card-text
      p.mx-4 It appears one or more of your intended recipients has not provided opt-in consent for SMS messaging. Review all tabs below prior to sending your SMS.
      v-tabs.contacts-data-container.mt-4.mx-4(
        v-model='currentTab',
        height='30',
        color='#ebebeb',
        slider-color='#235ea1'
      )
        v-tab.contacts-status-header.text-none(v-for='status in statuses', :key='status.id') {{ status.value }}
        v-tab-item(v-for='status in statuses', :key='status.id')
          v-card.elevation-0
            v-card-text.px-2.pb-2.pt-0
              .contacts-list-container
                p.primary--text.mt-2(v-if='!filteredContacts(status.id).length')
                  | There are no contacts with this status
                v-list.pa-0(v-if='status.id !== "denied"', dense)
                  v-list-tile.contact-tile
                    v-list-tile-action.pa-0.ma-0
                      v-checkbox.contacts-checkbox.pa-0(
                        v-if='filteredContacts(status.id).length',
                        :disabled='status.id === "approved"',
                        v-model='status.selectAll',
                        @click.native='onSelectAllToggle(status)',
                        color='#235ea1',
                        hide-details
                      )
                    v-list-tile-content.pa-0(v-if='filteredContacts(status.id).length')
                      v-list-tile-title.contacts-names Select All
                  v-list-tile.ml-3.contact-tile(v-for='contact in filteredContacts(status.id)')
                    v-list-tile-action.pa-0.ma-0
                      v-checkbox.contacts-checkbox(
                        v-model='contact.isSelected',
                        :disabled='contact.isDisabled || contact.isReadOnly',
                        @change='checkAvailableContacts()',
                        color='#235ea1',
                        hide-details
                      )
                    v-list-tile-content.pa-0
                      v-list-tile-title.contacts-names {{ contact.first_name }} {{ contact.last_name }}
                v-list(v-else)
                  v-list-tile.contact-tile(v-for='contact in filteredContacts(status.id)')
                    span.contacts-names {{ contact.first_name }} {{ contact.last_name }}
              p.caption.ma-0.pa-1.my-2(v-if='filteredContacts(status.id).length') {{ status.description }}
    v-layout(row, align-center, justify-center)
      v-card-actions.campaign-btn-container
        v-btn.mt-2.primary.continue-campaign-btn(
          @click='continueStartCampaign',
          :disabled='!hasAvailableContacts'
        ) 
          v-layout.py-2(column, align-center, justify-center) 
            span.continue-campaign-btn Start Campaign
            span.continue-campaign-btn.text-lowercase for selected contacts
    v-layout.pb-3.mt-1.font-weight-bold(row, align-center, justify-center)
      a.mt-2.text-pre.center-text(@click='goToLearnMore()') Learn more about texting consent
  v-card-actions.pa-3
    v-spacer
    v-btn(flat, @click='closeModal') Cancel
</template>
<script>
import Modal from '@/Core/mixins/modal';
import TitleBar from '@/Core/components/TitleBar';

export default {
  name: 'SmsConsentReview',
  components: {
    TitleBar,
  },
  mixins: [Modal],
  props: {
    contacts: Array,
  },
  data() {
    return {
      currentTab: null,
      hasAvailableContacts: false,
      statuses: [
        {
          id: 'not_requested',
          value: 'Not requested',
          description: `These contacts do not have Opt-In Consent and you have not requested consent yet. When 
            you click Start Campaign these contacts will receive your message along with an Opt-In 
            request.`,
          selectAll: true,
        },
        {
          id: 'pending',
          value: 'Pending',
          description: `These contacts have received an Opt-In request but have not responded. You can choose to 
            add them to this campaign anyway. An additional opt-in request will not be sent. We 
            recommend waiting for their opt-in response before adding them to any campaigns that 
            contain SMS.`,
          selectAll: false,
        },
        {
          id: 'approved',
          value: 'Approved',
          description: `These contacts have Opt-In Consent. They will be added to this campaign without any 
            further Opt-In requests.`,
          selectAll: true,
        },
        {
          id: 'denied',
          value: 'Denied',
          description: `These contacts have opted-out of SMS Messaging from you. Text messages to these 
            contacts cannot be sent.`,
          selectAll: false,
        },
      ],
    };
  },
  created() {
    this.contacts = this.getSelectableContacts();
    this.checkAvailableContacts();
  },
  methods: {
    checkAvailableContacts() {
      this.hasAvailableContacts = this.contacts.some(contact => {
        return contact.isSelected;
      });
    },
    continueStartCampaign() {
      const contactIds = this.getContactIds();
      this.$emit('continue', contactIds);
    },
    closeModal() {
      this.$emit('cancel');
    },
    goToLearnMore() {
      window.open('https://lonewolf.my.site.com/s/article/LD-Text-compliance-best-practices-LionDesk', '_blank');
    },
    filteredContacts(status) {
      return this.contacts.filter(contact => {
        if (contact.status === status) return contact;
      });
    },
    getSelectableContacts() {
      this.contacts.forEach(contact => {
        contact.isSelected = contact.status === 'approved' || contact.status === 'not_requested';
        contact.isReadOnly = contact.status === 'approved';
      });

      return this.contacts;
    },
    getContactIds() {
      return this.contacts
        .filter(contact => {
          if (contact.isSelected) {
            return contact;
          }
        })
        .map(contact => contact.id);
    },
    onSelectAllToggle(status) {
      this.contacts.forEach(contact => {
        if (contact.status === status.id) {
          contact.isSelected = status.selectAll;
        }
      });

      this.checkAvailableContacts();
    },
  },
};
</script>


<style lang="scss" scoped>
.v-dialog {
  max-width: 610px;
}
.v-list__tile__action {
  min-width: 24px;
  max-height: 30px;
}
.v-list__tile__content,
.v-list__tile__action {
  height: 30px;
}
.contacts-names {
  font-size: 14px;
}
.contacts-status-header {
  height: 30px;
  color: var(--v-primary-base) !important;
  font-weight: 500;
}
.continue-campaign-btn {
  width: 250px;
  height: auto;
  font-size: 13px;
}
.campaign-btn-container {
  border: none;
}
.continue-campaign-btnText {
  font-size: 16px;
  font-weight: 500;
}
.contacts-data-container {
  border: 1px solid var(--v-background-lighten4);
}
.contacts-checkbox {
  transform: scale(0.7);
}
.v-label .v-input {
  font-size: 10px !important;
}
.contact-tile {
  height: 30px;
}
.contacts-list-container {
  min-height: 100px;
  max-height: 200px;
  overflow: auto;
}
</style>
